import React, { useState, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useGetProductionJobsQuery, useGetProductionSummaryQuery } from '../../store/slices/apiSlice';
import type { ProductionStatus, JobType } from '../../utils/types';
import { Factory, AlertTriangle, Settings, Search, Filter } from 'lucide-react';
import { ProductionJobCard } from '../../components/production/ProductionJobCard';
import { OrderDetailsModal } from '../../components/common/OrderDetailsModal';
// import { EmailTestButton } from '../../components/common/EmailTestButton';

const TABS: Array<{ key: ProductionStatus; label: string; icon: React.ComponentType<any>; color: string }> = [
  { key: 'new_orders', label: 'New Orders', icon: Factory, color: 'blue' },
  { key: 'in_production', label: 'In Production', icon: Settings, color: 'green' },
  { key: 'problem_orders', label: 'Problem Orders', icon: AlertTriangle, color: 'red' },
];

export const ProductionPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProductionStatus>('new_orders');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [ordering, setOrdering] = useState('-created_at');
  const [viewOrdersModal, setViewOrdersModal] = useState<{
    jobId: string;
    customerName: string;
  } | null>(null);
  // const [testEmailAddress, setTestEmailAddress] = useState('');

  const PAGE_SIZE = 2000; // Fetch all jobs (no pagination); backend max 10000

  const params = useMemo(() => ({
    page: 1,
    page_size: PAGE_SIZE,
    production_status: activeTab,
    search: searchTerm || undefined,
    job_type: jobTypeFilter !== 'all' ? (jobTypeFilter as JobType) : undefined,
    ordering,
  }), [activeTab, searchTerm, jobTypeFilter, ordering]);

  const { data: jobsData, isLoading, isError, refetch } = useGetProductionJobsQuery(params);
  const { data: summary, isLoading: isSummaryLoading } = useGetProductionSummaryQuery();

  const jobs = jobsData?.results ?? [];

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setJobTypeFilter('all');
    setOrdering('-created_at');
  };
  
  const getTabCount = (tab: ProductionStatus) => {
    if (!summary) return 0;
    return summary[tab] || 0;
  };

  if (isError) {
    return (
      <AppShell>
        <PageLayout
          title="Production"
          subtitle="Production workflow management"
          bgColor={PAGE_COLORS.production.bgColor}
          textColor={PAGE_COLORS.production.textColor}
          borderColor={PAGE_COLORS.production.borderColor}
          icon={<Factory className="w-6 h-6" />}
        >
          <ContentCard title="Error Loading Production">
            <div className="text-center py-6">
              <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <p className="text-gray-600 mb-4">Unable to load production data</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageLayout
          title="Production"
        subtitle="Manage production workflow and job status"
          bgColor={PAGE_COLORS.production.bgColor}
          textColor={PAGE_COLORS.production.textColor}
          borderColor={PAGE_COLORS.production.borderColor}
        icon={<Factory className="w-6 h-6" />}
      >
        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {TABS.map((tab) => {
                const count = getTabCount(tab.key);
                const isActive = activeTab === tab.key;
                const Icon = tab.icon;
                
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`
                      whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${isActive
                        ? `border-${tab.color}-500 text-${tab.color}-600`
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    {!isSummaryLoading && (
                      <span className={`
                        inline-flex items-center px-3 py-1 rounded-full text-sm font-bold min-w-[24px] h-6
                        ${isActive
                          ? `bg-${tab.color}-100 text-${tab.color}-800 ring-2 ring-${tab.color}-200`
                          : 'bg-gray-100 text-gray-800'
                        }
                      `}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <ContentCard
          title={`Production Jobs - ${TABS.find(t => t.key === activeTab)?.label}`}
          subtitle="Search, filter and manage production workflow"
        >
          <div className="mb-6">
            {/* Email System Test Section - commented out */}
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">📧 Email System Testing</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Test the email configuration system
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <input 
                    type="email"
                    placeholder="your-email@example.com"
                    className="form-input text-sm w-64"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                  />
                  <EmailTestButton
                    recipientEmail={testEmailAddress}
                    accountId="production@aeglazing.co.uk"
                    onSuccess={() => console.log('Test email sent successfully')}
                    onError={(error) => console.error('Test email failed:', error)}
                  />
                </div>
              </div>
            </div> */}

            <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search customer, email, job #, postcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>

                <div className="hidden sm:flex items-center gap-2 text-gray-700">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>

                <select
                  value={jobTypeFilter}
                  onChange={(e) => setJobTypeFilter(e.target.value)}
                  className="form-input w-auto"
                >
                  <option value="all">All Types</option>
                  <option value="ga">GA (Glass Arrival)</option>
                  <option value="gc">GC (Glass Cutting)</option>
                  <option value="gap">GAP (Glass Processing)</option>
                </select>

                <select
                  value={ordering}
                  onChange={(e) => setOrdering(e.target.value)}
                  className="form-input w-auto"
                >
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="-glass_arrival_date">Glass Arrival (Latest First)</option>
                  <option value="glass_arrival_date">Glass Arrival (Earliest First)</option>
                  <option value="-manufacture_date">Manufacturing Date (Latest First)</option>
                  <option value="manufacture_date">Manufacturing Date (Earliest First)</option>
                  <option value="-potential_delivery_date">Delivery Date (Latest First)</option>
                  <option value="potential_delivery_date">Delivery Date (Earliest First)</option>
                  <option value="-total_amount">Amount (High-Low)</option>
                  <option value="total_amount">Amount (Low-High)</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button type="submit" variant="secondary">Search</Button>
                {(searchTerm || jobTypeFilter !== 'all' || ordering !== '-created_at') && (
                  <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
                )}
              </div>
            </form>
          </div>

          {/* Content Area */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : jobs.length === 0 ? (
            <EmptyState
              title={searchTerm ? 'No jobs found' : `No ${TABS.find(t => t.key === activeTab)?.label.toLowerCase()}`}
              description={searchTerm 
                ? `No jobs match "${searchTerm}" in ${TABS.find(t => t.key === activeTab)?.label.toLowerCase()}`
                : `There are currently no jobs in ${TABS.find(t => t.key === activeTab)?.label.toLowerCase()} status`
              }
              icon={<Factory className="w-12 h-12 text-gray-400" />}
            />
          ) : (
            <>
            {/* Job Cards List - content-visibility for performance with many cards */}
            <div
              className="space-y-4 mb-6"
              style={{ contain: 'layout style' }}
            >
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="[content-visibility:auto] [contain-intrinsic-size:auto_300px]"
                  >
                    <ProductionJobCard
                      job={job}
                      onStatusChange={() => {}}
                      onViewOrders={(jobId, customerName) => {
                        setViewOrdersModal({ jobId, customerName });
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </ContentCard>
      </PageLayout>

      {/* View Orders Modal */}
      {viewOrdersModal && (
        <OrderDetailsModal
          isOpen={true}
          onClose={() => setViewOrdersModal(null)}
          jobId={viewOrdersModal.jobId}
          title={`Order Details - ${viewOrdersModal.customerName}`}
          mode="view"
        />
      )}
    </AppShell>
  );
};
